import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, Lock, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { formatApiErrorDetail } from "../lib/api";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await login(email, password);
      toast.success("Welcome back, Admin");
      navigate("/admin");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Login failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-5 py-20 relative overflow-hidden" data-testid="admin-login-page">
      <div className="absolute inset-0 lightning-grid opacity-30" />
      <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-electric/15 rounded-full blur-3xl" />
      <Link to="/" className="absolute top-6 left-6 text-zinc-400 hover:text-electric flex items-center gap-2 text-sm" data-testid="admin-back-home">
        <ArrowLeft className="w-4 h-4" /> Back to site
      </Link>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-md"
      >
        <div className="flex flex-col items-center mb-8">
          <img src="/logo.png" alt="The Push Center" className="w-20 h-20 rounded-full ring-2 ring-electric/40 mb-5" />
          <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-2">
            Admin Access
          </div>
          <h1 className="font-display font-black text-4xl tracking-tighter">Welcome back.</h1>
        </div>

        <form onSubmit={submit} className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-8 space-y-5" data-testid="admin-login-form">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold mb-2">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-[#050505] border border-white/10 text-white placeholder-zinc-600 focus:outline-none focus:border-electric focus:ring-1 focus:ring-electric/30 transition"
              placeholder="admin@pushcenter.org"
              data-testid="admin-login-email"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold mb-2">Password</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-[#050505] border border-white/10 text-white placeholder-zinc-600 focus:outline-none focus:border-electric focus:ring-1 focus:ring-electric/30 transition"
              placeholder="••••••••"
              data-testid="admin-login-password"
            />
          </div>

          {error && (
            <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg p-3" data-testid="admin-login-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 bg-electric text-black px-7 py-4 rounded-lg font-bold tracking-wide hover:bg-[#4ade80] transition-all duration-300 disabled:opacity-60"
            data-testid="admin-login-submit"
          >
            <Lock className="w-4 h-4" />
            {submitting ? "Signing in..." : "Sign In"}
          </button>
        </form>

        <div className="mt-6 text-center text-xs text-zinc-500 flex items-center justify-center gap-1.5">
          <Zap className="w-3 h-3 text-electric" /> Authorized personnel only
        </div>
      </motion.div>
    </main>
  );
}
